import { useEffect, useRef, useState, useCallback } from 'react';
import io from 'socket.io-client';

const SAMPLING_RATE = 16000;
const BUFFER_SIZE = 4096;

export const useVoiceChat = (dialogId, settingsId = 'default', enabled = true) => {
    const [isConnected, setIsConnected] = useState(false);
    const [isRecording, setIsRecording] = useState(false);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [transcript, setTranscript] = useState('');
    const [assistantText, setAssistantText] = useState('');
    const [messages, setMessages] = useState([]);
    const [error, setError] = useState(null);

    const socketRef = useRef(null);
    const audioContextRef = useRef(null);
    const processorRef = useRef(null);
    const streamRef = useRef(null);

    // Removed unused refs: audioQueueRef, isPlayingRef
    const nextStartTimeRef = useRef(0);
    const activeSourceRef = useRef(null);
    const playbackSampleRateRef = useRef(24000); // Default, will update
    const hasHeaderRef = useRef(false);
    const pendingTextRef = useRef('');
    const transcriptRef = useRef('');

    // Initialize AudioContext
    useEffect(() => {
        // Cross-browser support
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        if (AudioContext) {
            audioContextRef.current = new AudioContext({ sampleRate: SAMPLING_RATE });
        }
        return () => {
            if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
                audioContextRef.current.close().catch(console.error);
            }
        };
    }, []);

    // WAV Header Parser
    const parseWavHeader = useCallback((buffer) => {
        const view = new DataView(buffer);
        if (String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3)) !== 'RIFF') return null;
        // Channels at offset 22 (2 bytes)
        const channels = view.getUint16(22, true);
        // Sample rate at offset 24 (4 bytes)
        const sampleRate = view.getUint32(24, true);
        return { channels, sampleRate };
    }, []);

    const stopPlayback = useCallback(() => {
        // WAV cleanup
        if (activeSourceRef.current) {
            try {
                activeSourceRef.current.stop();
            } catch (e) { }
            activeSourceRef.current = null;
        }

        // MP3 cleanup
        if (audioObjectRef.current) {
            try {
                audioObjectRef.current.pause();
                // We might revoke object URL later or reuse
            } catch (e) { }
            audioObjectRef.current = null;
        }
        if (sourceBufferRef.current) {
            try {
                if (sourceBufferRef.current.updating) {
                    sourceBufferRef.current.abort();
                }
            } catch (e) { }
            sourceBufferRef.current = null;
        }
        if (mediaSourceRef.current) {
            if (mediaSourceRef.current.readyState === 'open') {
                // Close the stream
                try {
                    mediaSourceRef.current.endOfStream();
                } catch (e) { }
            }
            mediaSourceRef.current = null;
        }
        mp3QueueRef.current = [];
        isSourceOpenRef.current = false;

        // Reset scheduling
        nextStartTimeRef.current = 0;
        hasHeaderRef.current = false; // Reset header flag for next turn
        setIsSpeaking(false);
    }, []);

    const schedulePlayback = useCallback((buffer) => {
        const source = audioContextRef.current.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContextRef.current.destination);

        const currentTime = audioContextRef.current.currentTime;
        // Schedule to start after the previous one finishes, or now if queue empty
        const startTime = Math.max(currentTime, nextStartTimeRef.current);

        source.start(startTime);
        nextStartTimeRef.current = startTime + buffer.duration;

        // Track active sources to stop them on interrupt
        activeSourceRef.current = source;

        source.onended = () => {
            if (audioContextRef.current.currentTime >= nextStartTimeRef.current) {
                setIsSpeaking(false);
            }
        };

        // If we have pending text, show it now that audio is starting
        if (pendingTextRef.current) {
            const text = pendingTextRef.current;
            setMessages(prev => [...prev, { sender: 'assistant', text, id: Date.now() }]);
            // We can still set assistantText if we want to track 'last response', 
            // but for UI we rely on messages. 
            // setAssistantText(text); 
            pendingTextRef.current = '';
        }

        setIsSpeaking(true);
    }, []);

    // MP3 / MediaSource Refs
    const mediaSourceRef = useRef(null);
    const audioObjectRef = useRef(null);
    const sourceBufferRef = useRef(null);
    const mp3QueueRef = useRef([]);
    const isMp3ModeRef = useRef(false);
    const isSourceOpenRef = useRef(false);

    // Initial check to determine format for the current response stream
    const detectFormat = useCallback((buffer) => {
        const view = new DataView(buffer);
        // Check for RIFF (WAV)
        if (buffer.byteLength >= 4 && String.fromCharCode(view.getUint8(0), view.getUint8(1), view.getUint8(2), view.getUint8(3)) === 'RIFF') {
            return 'wav';
        }
        // Assume MP3 if not WAV (could be more robust, but acceptable for now)
        return 'mp3';
    }, []);

    const processMp3Queue = useCallback(() => {
        if (!sourceBufferRef.current || sourceBufferRef.current.updating || mp3QueueRef.current.length === 0) return;

        const chunk = mp3QueueRef.current.shift();
        try {
            sourceBufferRef.current.appendBuffer(chunk);
        } catch (e) {
            console.error('[VoiceChat] SourceBuffer append error:', e);
        }
    }, []);

    const initMp3Playback = useCallback(() => {
        if (mediaSourceRef.current) return;

        const mediaSource = new MediaSource();
        mediaSourceRef.current = mediaSource;

        const audio = new Audio();
        audio.src = URL.createObjectURL(mediaSource);
        audioObjectRef.current = audio;

        // Optional: route through AudioContext if we want gain control etc.
        // const source = audioContextRef.current.createMediaElementSource(audio);
        // source.connect(audioContextRef.current.destination);

        mediaSource.addEventListener('sourceopen', () => {
            isSourceOpenRef.current = true;
            try {
                // ElevenLabs usually sends 44.1kHz mp3. 'audio/mpeg' covers it.
                const sourceBuffer = mediaSource.addSourceBuffer('audio/mpeg');
                sourceBufferRef.current = sourceBuffer;

                sourceBuffer.addEventListener('updateend', () => {
                    processMp3Queue();
                });

                processMp3Queue(); // Try processing if anything queued
            } catch (e) {
                console.error('[VoiceChat] Error adding SourceBuffer:', e);
            }
        });

        audio.play().catch(e => console.error('[VoiceChat] Audio play error:', e));
        setIsSpeaking(true);

        // Show pending text immediately when starting playback (similar to WAV path)
        if (pendingTextRef.current) {
            const text = pendingTextRef.current;
            setMessages(prev => [...prev, { sender: 'assistant', text, id: Date.now() }]);
            pendingTextRef.current = '';
        }

        audio.onended = () => {
            setIsSpeaking(false);
        };
    }, [processMp3Queue]);



    const processWavChunk = useCallback((audioData) => {
        // Convert Int16 to Float32
        const int16Data = new Int16Array(audioData);
        const float32Data = new Float32Array(int16Data.length);
        for (let i = 0; i < int16Data.length; i++) {
            float32Data[i] = int16Data[i] / 32768.0;
        }

        const buffer = audioContextRef.current.createBuffer(1, float32Data.length, playbackSampleRateRef.current || 24000);
        buffer.getChannelData(0).set(float32Data);
        schedulePlayback(buffer);
    }, [schedulePlayback]);

    const playAudioChunk = useCallback(async (arrayBuffer) => {
        if (!audioContextRef.current) return;

        // Resume context if suspended
        if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
        }

        try {
            // Detect format on first chunk of a "turn"
            if (!hasHeaderRef.current) {
                const format = detectFormat(arrayBuffer);
                if (format === 'wav') {
                    const header = parseWavHeader(arrayBuffer);
                    if (header) {
                        console.log('[VoiceChat] Detected WAV header:', header);
                        playbackSampleRateRef.current = header.sampleRate;
                        isMp3ModeRef.current = false;
                        hasHeaderRef.current = true;

                        // Strip header for WAV processing
                        const bytes = new Uint8Array(arrayBuffer);
                        let dataOffset = 12;
                        while (dataOffset < bytes.length - 8) {
                            let id = String.fromCharCode(bytes[dataOffset], bytes[dataOffset + 1], bytes[dataOffset + 2], bytes[dataOffset + 3]);
                            let size = (bytes[dataOffset + 7] << 24) | (bytes[dataOffset + 6] << 16) | (bytes[dataOffset + 5] << 8) | bytes[dataOffset + 4];
                            if (id === 'data') {
                                dataOffset += 8;
                                break;
                            }
                            dataOffset += 8 + size;
                        }
                        const audioData = (dataOffset < bytes.length) ? arrayBuffer.slice(dataOffset) : arrayBuffer.slice(44);
                        processWavChunk(audioData);
                        return;
                    }
                } else {
                    // MP3
                    console.log('[VoiceChat] Detected non-WAV data, switching to MP3 mode');
                    isMp3ModeRef.current = true;
                    hasHeaderRef.current = true;
                    initMp3Playback();
                }
            }

            if (isMp3ModeRef.current) {
                // MP3 Logic
                mp3QueueRef.current.push(arrayBuffer);
                if (isSourceOpenRef.current) {
                    processMp3Queue();
                }
            } else {
                // WAV Logic (Subsequent chunks)
                processWavChunk(arrayBuffer);
            }

        } catch (e) {
            console.error("[VoiceChat] Error processing audio chunk", e);
        }
    }, [detectFormat, initMp3Playback, parseWavHeader, processMp3Queue, processWavChunk]);



    const stopRecording = useCallback(() => {
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }

        if (processorRef.current) {
            processorRef.current.disconnect();
            processorRef.current = null;
        }

        if (socketRef.current?.connected) {
            socketRef.current.emit('stop-recognition');
        }

        stopPlayback();
        setIsRecording(false);
    }, [stopPlayback]);

    const connect = useCallback(() => {
        if (!dialogId || !enabled) return;
        if (socketRef.current?.connected) return;

        const token = process.env.REACT_APP_CHAT_API_TOKEN;
        const voiceUrl = process.env.REACT_APP_CHAT_API_VOICE;

        if (!voiceUrl) {
            setError('Voice chat URL not configured');
            return;
        }

        // Append the namespace to the URL
        const namespaceUrl = `${voiceUrl}/voice-chat`;

        console.log('[VoiceChat] Connecting with:', { dialogId, settingsId, namespaceUrl });

        const socket = io(namespaceUrl, {
            auth: { token },
            query: { dialog_id: dialogId, settings_id: settingsId },
            transports: ['websocket'],
            path: '/api-voice/socket.io'
        });

        socket.on('connect', () => {
            setIsConnected(true);
            setError(null);
        });

        socket.on('disconnect', () => {
            setIsConnected(false);
            setIsRecording(false);
            setIsSpeaking(false);
        });

        socket.on('connect_error', (err) => {
            console.error('Connection error:', err);
            setError(err.message);
            setIsConnected(false);
        });

        socket.on('recognition-started', (data) => {
            // Started
            setAssistantText('');
            pendingTextRef.current = '';
        });

        socket.on('recognition-result', (data) => {
            if (data && data.transcript) {
                setTranscript(data.transcript);
                transcriptRef.current = data.transcript;
            }
        });

        socket.on('assistant-text', (data) => {
            if (data && data.text) {
                // User turn is effectively done when assistant replies.
                // We need to capture the user's transcript available at this moment.
                // Since this closure has stale state, we'll use the transcriptRef we're about to add.
                if (transcriptRef.current) {
                    setMessages(prev => [...prev, { sender: 'user', text: transcriptRef.current, id: Date.now() }]);
                    transcriptRef.current = ''; // Clear ref so we don't duplicate? Or just update state?
                    setTranscript(''); // Clear visual transcript
                }

                pendingTextRef.current = data.text;
                // previously: setAssistantText(data.text);
            }
        });

        socket.on('audio-response', (arrayBuffer) => {
            playAudioChunk(arrayBuffer);
        });

        socket.on('audio-response-complete', () => {
            // complete
            if (isMp3ModeRef.current && mediaSourceRef.current && mediaSourceRef.current.readyState === 'open') {
                try {
                    mediaSourceRef.current.endOfStream();
                } catch (e) {
                    console.error('Error ending MediaSource stream:', e);
                }
            }
        });

        socket.on('assistant-interrupted', () => {
            stopPlayback();
        });

        socket.on('error', (data) => {
            console.error('Socket error:', data);
            setError(typeof data === 'object' ? data.message : data);
        });

        socketRef.current = socket;
    }, [dialogId, settingsId, enabled, playAudioChunk, stopPlayback]);

    const disconnect = useCallback(() => {
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;
        }
        setIsConnected(false);
        stopRecording();
        stopPlayback();
    }, [stopRecording, stopPlayback]);

    // Cleanup on unmount
    useEffect(() => {
        return () => disconnect();
    }, [disconnect]);

    useEffect(() => {
        if (enabled && dialogId) {
            connect();
        }
    }, [enabled, dialogId, connect]);

    const startRecording = async () => {
        if (!isConnected) {
            // connection logic handled by effect
        }

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                audio: {
                    channelCount: 1,
                    sampleRate: 16000,
                    echoCancellation: true,
                    noiseSuppression: true
                }
            });
            streamRef.current = stream;

            // Resume context
            if (audioContextRef.current?.state === 'suspended') {
                await audioContextRef.current.resume();
            }

            const source = audioContextRef.current.createMediaStreamSource(stream);
            // Create ScriptProcessor (deprecated but stable for raw PCM access)
            const inputSampleRate = audioContextRef.current.sampleRate;

            const processor = audioContextRef.current.createScriptProcessor(BUFFER_SIZE, 1, 1);

            processor.onaudioprocess = (e) => {
                if (!socketRef.current?.connected) return;

                const inputData = e.inputBuffer.getChannelData(0);

                // Downsample if needed, otherwise copy
                const targetRate = 16000;

                // If input is already 16k (which it should be with constraints/context), this is 1
                const ratio = inputSampleRate / targetRate;

                const outputLength = Math.ceil(inputData.length / ratio);
                const pcm16 = new Int16Array(outputLength);

                for (let i = 0; i < outputLength; i++) {
                    const offset = Math.floor(i * ratio);
                    const s = Math.max(-1, Math.min(1, inputData[offset]));
                    pcm16[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
                }

                socketRef.current.emit('audio-chunk', pcm16.buffer);
            };

            // Mute local output to prevent feedback/hearing yourself
            const gain = audioContextRef.current.createGain();
            gain.gain.value = 0;
            source.connect(processor);
            processor.connect(gain);
            gain.connect(audioContextRef.current.destination);

            processorRef.current = processor;

            socketRef.current.emit('start-recognition', { languageCode: 'en-US' });

            // Add previous assistant response to history before clearing
            if (assistantText) {
                setMessages(prev => [...prev, { sender: 'assistant', text: assistantText, id: Date.now() }]);
            }

            // Clear previous response immediately for better UX
            setAssistantText('');
            pendingTextRef.current = '';

            setIsRecording(true);

        } catch (err) {
            console.error('Error starting recording:', err);
            setError('Could not access microphone');
        }
    };

    return {
        isConnected,
        isRecording,
        isSpeaking,
        transcript,
        assistantText,
        messages,
        startRecording,
        stopRecording,
        stopPlayback,
        error
    };
};

export default useVoiceChat;
